import React, { useCallback, useState } from 'react';

// DEPENDENCIES
import clsx from 'clsx';
import { useDispatch } from 'react-redux';

// FUNCTIONS/HELPERS
import { sidebarActions } from 'actions.js';
import useRecordView from 'helpers/getRecordView';

// CONTEXT
import AMFIProductsProvider, { useAMFIProducts } from '../helpers/AMFIProductsProvider';

// GLOBAL COMPONENTS
import Form from 'core/form/Form';

// LOCAL COMPONENTS

import ConfirmCancel from '../parts/ConfirmCancel';
import AMProducts from '../parts/AMProducts';
import FIProducts from '../parts/FIProducts';
import FormRow from '../tools/FormRow';
import FormCol from '../tools/FormCol';

// STYLES
import styles from './amfiProducts.module.scss';

// MAIN COMPONENT
const AMFIProductsForm = () => {
  const [cancelledIndex, setCancelledIndex] = useState({});
  const dispatch = useDispatch();
  const { isSales, isView } = useRecordView();

  const {
    amGrossTime,
    amProducts,
    amProductsTotalGross,
    fiProducts,
    fiProductsTotalGross,
    fmGrossTime,
    totalBackendGross,
    readOnly
  } = useAMFIProducts();

  // CONFIRM CANCEL SIDEBAR
  const handleCloseConfirmCancel = useCallback(() => {
    setCancelledIndex();
  }, []);

  const handleCancelProduct = useCallback(
    index => {
      setCancelledIndex(index);
      dispatch(sidebarActions.toggle('amFinanceConfirmCancelSidebar', true));
    },
    [dispatch]
  );

  return (
    <>
      <Form.Body className={clsx(styles.form, isView && styles.view)} plaintext={readOnly}>
        <Form.Col>
          <FormRow className="d-none d-md-flex">
            <FormCol cols={{ xs: 24, md: 5 }} className={clsx(!isSales && 'justify-content-end')}>
              <>Product Name</>
            </FormCol>
            <FormCol cols={{ xs: 24, md: 5 }}>
              <>Code</>
            </FormCol>
            <FormCol cols={{ xs: 24, md: 5 }}>
              <>Selling Price</>
            </FormCol>
            <FormCol cols={{ xs: 24, md: 5 }}>
              <>Cost</>
            </FormCol>
            <FormCol cols={{ xs: 24, md: 4 }}>
              <>Gross</>
            </FormCol>
          </FormRow>
        </Form.Col>
        <AMProducts onCancelProduct={handleCancelProduct} />
        <Form.Col>
          <FormRow>
            <FormCol cols={{ xs: 24, md: 5 }}>
              <Form.Label className={clsx(!isSales && 'justify-content-end')}>Total AM Gross</Form.Label>
            </FormCol>
            <FormCol cols={{ xs: 24, md: { span: 14, offset: 5 } }}>
              {amProducts?.length ? (
                <Form.PlainText schema="dollars" value={amProductsTotalGross} />
              ) : !isView ? (
                <Form.Control name="am_gross" schema="dollars" plaintext={readOnly} />
              ) : null}
            </FormCol>
          </FormRow>
        </Form.Col>
        <Form.Col>
          <FormRow>
            <FormCol cols={{ xs: 24, md: 5 }}>
              <Form.Label className={clsx(!isSales && 'justify-content-end')}>Time AM Gross Entered</Form.Label>
            </FormCol>
            <FormCol cols={{ xs: 24, md: { span: 14, offset: 5 } }}>
              <Form.PlainText value={amGrossTime} />
            </FormCol>
          </FormRow>
        </Form.Col>
        <FIProducts />
        <Form.Col>
          <FormRow>
            <FormCol cols={{ xs: 24, md: 5 }}>
              <Form.Label className={clsx(!isSales && 'justify-content-end')}>Total F&amp;I Gross</Form.Label>
            </FormCol>
            <FormCol cols={{ xs: 24, md: { span: 14, offset: 5 } }}>
              {fiProducts?.length ? (
                <Form.PlainText schema="dollars" value={fiProductsTotalGross} />
              ) : !isView ? (
                <Form.Control name="fm_gross" schema="dollars" plaintext={readOnly} />
              ) : null}
            </FormCol>
          </FormRow>
        </Form.Col>
        <Form.Col>
          <FormRow>
            <FormCol cols={{ xs: 24, md: 5 }}>
              <Form.Label className={clsx(!isSales && 'justify-content-end')}>Time F&amp;I Gross Entered</Form.Label>
            </FormCol>
            <FormCol cols={{ xs: 24, md: { span: 14, offset: 5 } }}>
              <Form.PlainText value={fmGrossTime} />
            </FormCol>
          </FormRow>
        </Form.Col>
        <Form.Col>
          <FormRow>
            <FormCol cols={{ xs: 24, md: 5 }}>
              <Form.Label className={clsx(!isSales && 'justify-content-end')}>Total Backend Gross</Form.Label>
            </FormCol>
            <FormCol cols={{ xs: 24, md: { span: 14, offset: 5 } }}>
              <Form.PlainText schema="dollars" value={totalBackendGross} />
            </FormCol>
          </FormRow>
        </Form.Col>
      </Form.Body>
      <ConfirmCancel onClose={handleCloseConfirmCancel} cancelledIndex={cancelledIndex} />
    </>
  );
};

// EXPORT
export default function AMFIProducts() {
  return (
    <AMFIProductsProvider>
      <AMFIProductsForm />
    </AMFIProductsProvider>
  );
}
